import { type ReactNode, useEffect, useMemo, useState } from 'react';
import type {
  ScheduleOwnerDto,
  ScheduleResponse,
  TfTrackRow,
} from '@verybigthings/trackframe-shared/schemas';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Alert } from '@/components/ui/alert';
import { hub } from '@/lib/hub-client';
import {
  buildScheduleCreateBody,
  buildSchedulePatchBody,
  createScheduleFormValues,
  getWholePlanPreapprovalWarning,
  mapScheduleApiError,
  scheduleToFormValues,
  validateScheduleForm,
  WHOLE_PLAN_PREAPPROVAL_WARNING,
  type ScheduleFormValues,
  type ScheduleValidationHints,
} from './schedule-ui-model';

interface ScheduleFormDialogProps {
  open: boolean;
  mode: 'create' | 'edit';
  schedule?: ScheduleOwnerDto | null;
  initialTracks: readonly TfTrackRow[];
  onClose: () => void;
  onSaved: (response: ScheduleResponse) => void;
  onVersionConflict?: () => void | Promise<void>;
}

const VERSION_CONFLICT_MESSAGE = 'This schedule changed since you opened it. Refresh and try again.';

export function ScheduleFormDialog({
  open,
  mode,
  schedule,
  initialTracks,
  onClose,
  onSaved,
  onVersionConflict,
}: ScheduleFormDialogProps) {
  const [values, setValues] = useState<ScheduleFormValues>(() => createScheduleFormValues());
  const [originalSchedule, setOriginalSchedule] = useState<ScheduleOwnerDto | null>(schedule ?? null);
  const [tracks, setTracks] = useState<TfTrackRow[]>(() => [...initialTracks]);
  const [fieldErrors, setFieldErrors] = useState<ScheduleValidationHints>({});
  const [formError, setFormError] = useState<string | null>(null);
  const [tracksError, setTracksError] = useState<string | null>(null);
  const [isLoadingTracks, setIsLoadingTracks] = useState(false);
  const [isLoadingSchedule, setIsLoadingSchedule] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [idempotencyKey, setIdempotencyKey] = useState<string>(() => createIdempotencyKey(mode));

  useEffect(() => {
    if (!open) return;

    const timezone = resolveLocalTimezone();
    setFieldErrors({});
    setFormError(null);
    setTracksError(null);
    setTracks([...initialTracks]);
    setIdempotencyKey(createIdempotencyKey(mode));

    if (mode === 'create') {
      setOriginalSchedule(null);
      setValues(createScheduleFormValues({ timezone }));
      setIsLoadingSchedule(false);
    } else if (schedule) {
      setOriginalSchedule(schedule);
      setValues(scheduleToFormValues(schedule));
    }
  }, [initialTracks, mode, open, schedule]);

  useEffect(() => {
    if (!open || mode !== 'create') return;

    let cancelled = false;
    setIsLoadingTracks(true);
    hub.tracksList()
      .then((result) => {
        if (cancelled) return;
        setTracks(result);
        setTracksError(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setTracks([]);
        setTracksError(`Readable tracks could not be loaded: ${mapScheduleApiError(error)}`);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingTracks(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, open]);

  useEffect(() => {
    if (!open || mode !== 'edit' || !schedule) return;

    let cancelled = false;
    setIsLoadingSchedule(true);
    hub.schedulesGet({ params: { id: schedule.id } })
      .then((response) => {
        if (cancelled) return;
        setOriginalSchedule(response.schedule);
        setValues(scheduleToFormValues(response.schedule));
        setFormError(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setFormError(`Current schedule values could not be refreshed: ${mapScheduleApiError(error)}`);
      })
      .finally(() => {
        if (!cancelled) setIsLoadingSchedule(false);
      });

    return () => {
      cancelled = true;
    };
  }, [mode, open, schedule]);

  const trackById = useMemo(() => new Map(tracks.map((track) => [track.id, track])), [tracks]);
  const selectedTrack = trackById.get(values.trackId);
  const preapprovalWarning = getWholePlanPreapprovalWarning(values.wholePlanPreapproved);
  const dialogTitle = mode === 'create' ? 'Create scheduled action' : 'Edit scheduled action';
  const dialogDescription = mode === 'create'
    ? 'Create an owner-scoped schedule for a readable track.'
    : 'Update schedule fields. Track ownership cannot be changed after creation.';

  const setField = <K extends keyof ScheduleFormValues>(field: K, value: ScheduleFormValues[K]) => {
    setValues((current) => ({ ...current, [field]: value }));
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
    setFormError(null);
  };

  const submit = async () => {
    const validation = validateScheduleForm(values);
    const blockingErrors: ScheduleValidationHints = { ...validation };
    delete blockingErrors.wholePlanPreapproved;

    if (Object.values(blockingErrors).some(Boolean)) {
      setFieldErrors(blockingErrors);
      setFormError('Fix the highlighted fields before saving.');
      return;
    }

    setFieldErrors({});
    setFormError(null);
    setIsSubmitting(true);
    try {
      const response = mode === 'create'
        ? await hub.schedulesCreate({ body: buildScheduleCreateBody(values), idempotencyKey })
        : await hub.schedulesUpdate({
          params: { id: requireOriginalSchedule(originalSchedule).id },
          body: buildSchedulePatchBody(requireOriginalSchedule(originalSchedule), values),
          idempotencyKey,
        });
      onSaved(response);
      onClose();
    } catch (error) {
      const mapped = mapScheduleApiError(error);
      setFormError(mapped);
      if (mapped === VERSION_CONFLICT_MESSAGE) await onVersionConflict?.();
    } finally {
      setIsSubmitting(false);
    }
  };

  const submitDisabled = isSubmitting
    || isLoadingTracks
    || isLoadingSchedule
    || (mode === 'create' && !!tracksError);

  return (
    <Dialog
      open={open}
      onClose={isSubmitting ? () => undefined : onClose}
      title={dialogTitle}
      description={dialogDescription}
      className="max-w-3xl"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>Cancel</Button>
          <Button onClick={() => void submit()} disabled={submitDisabled}>
            {isSubmitting ? 'Saving…' : mode === 'create' ? 'Create schedule' : 'Save changes'}
          </Button>
        </>
      }
    >
      <form
        className="space-y-4"
        onSubmit={(event) => {
          event.preventDefault();
          if (!submitDisabled) void submit();
        }}
      >
        {formError ? <Alert>{formError}</Alert> : null}

        {tracksError ? <Alert variant="warning">{tracksError}</Alert> : null}

        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Track" htmlFor="schedule-track" error={fieldErrors.trackId}>
            {mode === 'create' ? (
              <select
                id="schedule-track"
                className="h-9 w-full rounded-md border bg-background px-3 py-1 text-sm disabled:cursor-not-allowed disabled:opacity-50"
                value={values.trackId}
                disabled={isLoadingTracks || !!tracksError}
                aria-invalid={!!fieldErrors.trackId}
                aria-describedby={fieldErrors.trackId ? 'schedule-track-error' : undefined}
                onChange={(event) => setField('trackId', event.target.value)}
              >
                <option value="">Select a readable track</option>
                {tracks.map((track) => (
                  <option key={track.id} value={track.id}>{track.name}</option>
                ))}
              </select>
            ) : (
              <div className="rounded-md border bg-muted/40 px-3 py-2 text-sm">
                <div className="font-medium">{selectedTrack?.name ?? values.trackId}</div>
                <div className="font-mono text-xs text-muted-foreground">{values.trackId}</div>
                <input type="hidden" name="trackId" value={values.trackId} />
              </div>
            )}
          </Field>

          <Field label="Cron schedule" htmlFor="schedule-expr" error={fieldErrors.scheduleExpr}>
            <Input
              id="schedule-expr"
              value={values.scheduleExpr}
              placeholder="0 9 * * 1"
              aria-invalid={!!fieldErrors.scheduleExpr}
              aria-describedby={fieldErrors.scheduleExpr ? 'schedule-expr-error' : 'schedule-expr-help'}
              onChange={(event) => setField('scheduleExpr', event.target.value)}
            />
            {!fieldErrors.scheduleExpr ? (
              <p id="schedule-expr-help" className="text-xs text-muted-foreground">Five fields only: minute hour day-of-month month day-of-week.</p>
            ) : null}
          </Field>

          <Field label="Timezone" htmlFor="schedule-timezone" error={fieldErrors.timezone}>
            <Input
              id="schedule-timezone"
              value={values.timezone}
              placeholder="America/New_York"
              aria-invalid={!!fieldErrors.timezone}
              aria-describedby={fieldErrors.timezone ? 'schedule-timezone-error' : undefined}
              onChange={(event) => setField('timezone', event.target.value)}
            />
          </Field>

          <Field label="Allowed tools" htmlFor="schedule-allowlist" error={fieldErrors.toolAllowlistText}>
            <textarea
              id="schedule-allowlist"
              className="min-h-24 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={values.toolAllowlistText}
              placeholder="notion.search\nnotion.update"
              aria-invalid={!!fieldErrors.toolAllowlistText}
              aria-describedby={fieldErrors.toolAllowlistText ? 'schedule-allowlist-error' : 'schedule-allowlist-help'}
              onChange={(event) => setField('toolAllowlistText', event.target.value)}
            />
            {!fieldErrors.toolAllowlistText ? (
              <p id="schedule-allowlist-help" className="text-xs text-muted-foreground">Enter one tool per line or separate tools with commas. The Hub validates the final allowlist against the track.</p>
            ) : null}
          </Field>
        </div>

        <Field label="Prompt" htmlFor="schedule-prompt" error={fieldErrors.prompt}>
          <textarea
            id="schedule-prompt"
            className="min-h-32 w-full rounded-md border border-input bg-background px-3 py-2 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
            value={values.prompt}
            placeholder="Describe exactly what the scheduled agent should do."
            aria-invalid={!!fieldErrors.prompt}
            aria-describedby={fieldErrors.prompt ? 'schedule-prompt-error' : undefined}
            onChange={(event) => setField('prompt', event.target.value)}
          />
        </Field>

        <div className="grid gap-3 md:grid-cols-2">
          <label className="flex items-start gap-3 rounded-md border px-3 py-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={values.enabled}
              onChange={(event) => setField('enabled', event.target.checked)}
            />
            <span>
              <span className="font-medium">Enabled</span>
              <span className="block text-xs text-muted-foreground">Disabled schedules are paused and will not run.</span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-md border px-3 py-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={values.wholePlanPreapproved}
              aria-describedby="schedule-preapproval-warning"
              onChange={(event) => setField('wholePlanPreapproved', event.target.checked)}
            />
            <span>
              <span className="font-medium">Whole-plan preapproval</span>
              <span id="schedule-preapproval-warning" className="block text-xs text-amber-700">
                {preapprovalWarning ?? WHOLE_PLAN_PREAPPROVAL_WARNING}
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 rounded-md border px-3 py-2 text-sm">
            <input
              type="checkbox"
              className="mt-1"
              checked={values.notifyOnSuccess}
              onChange={(event) => setField('notifyOnSuccess', event.target.checked)}
            />
            <span>
              <span className="font-medium">Notify me on success</span>
              <span className="block text-xs text-muted-foreground">Off by default. Failures, denials, pauses, and runs waiting for approval always notify you.</span>
            </span>
          </label>
        </div>
      </form>
    </Dialog>
  );
}

function Field({
  label,
  htmlFor,
  error,
  children,
}: {
  label: string;
  htmlFor: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <div className="space-y-1.5">
      <label htmlFor={htmlFor} className="text-xs font-medium text-muted-foreground">
        {label}
      </label>
      {children}
      {error ? (
        <p id={`${htmlFor}-error`} role="alert" className="text-xs text-destructive">
          {error}
        </p>
      ) : null}
    </div>
  );
}

function requireOriginalSchedule(schedule: ScheduleOwnerDto | null): ScheduleOwnerDto {
  if (!schedule) throw new Error('A schedule must be loaded before editing.');
  return schedule;
}

function resolveLocalTimezone(): string {
  try {
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return timezone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

function createIdempotencyKey(mode: 'create' | 'edit'): string {
  const random = typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `admin-schedule-${mode}-${random}`;
}
